import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import StatCard from "@/components/StatCard";
import api from "@/lib/api";
import { LayoutDashboard, ShoppingBag, Truck, ScrollText, BarChart3, Settings, PackageCheck, TrendingUp, Trophy, FileCheck2, FileSpreadsheet, Store } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

// School-facing navigation — RFQ-oriented procurement (no direct-pay commerce).
// Prices/margins are stripped at the API layer for school_admin role.
const NAV = [
  { to: "/school/dashboard", label: "Overview", icon: LayoutDashboard },
  { to: "/school/order", label: "Request Quotation", icon: FileSpreadsheet, highlight: true },
  { to: "/school/rfq-history", label: "My RFQs", icon: ScrollText },
  { to: "/school/track", label: "Track Deliveries", icon: Truck },
  { to: "/school/reports", label: "Reports", icon: BarChart3 },
  { to: "/school/settings", label: "Settings", icon: Settings },
];
export { NAV as SCHOOL_NAV };

export default function SchoolDashboard() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/dashboard/school").then((r) => setData(r.data)); }, []);
  const budgetChart = data ? [
    { name: "Allocated", value: data.total_allocated },
    { name: "Utilized", value: data.total_utilized },
    { name: "Savings", value: data.savings },
  ] : [];

  return (
    <DashboardLayout nav={NAV} title="School Overview">
      {!data ? <div className="text-slate-500">Loading…</div> : (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
            <StatCard testid="kpi-orders" label="Total Orders" value={data.total_orders} icon={PackageCheck} tone="blue" />
            <StatCard testid="kpi-approvals" label="Pending Approvals" value={data.pending_approvals} icon={FileCheck2} tone="amber" />
            <StatCard testid="kpi-rfqs" label="Open RFQs" value={data.open_rfqs} icon={FileSpreadsheet} tone="blue" />
            <StatCard testid="kpi-vendors" label="Vendors" value={data.vendor_count} icon={Store} tone="slate" />
            <StatCard testid="kpi-utilization" label="Budget Utilization" value={`${data.budget_utilization}%`} icon={TrendingUp} tone="green" />
            <StatCard testid="kpi-savings" label="Savings" value={`₹${Number(data.savings).toLocaleString('en-IN')}`} icon={Trophy} tone="green" />
          </div>
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="sl-card p-6 lg:col-span-2">
              <h3 className="font-heading font-semibold text-slate-900">Budget snapshot</h3>
              <div className="mt-4 h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={budgetChart}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="name" stroke="#64748B" />
                    <YAxis stroke="#64748B" />
                    <Tooltip formatter={(v) => `₹${Number(v).toLocaleString('en-IN')}`} />
                    <Bar dataKey="value" fill="#2563EB" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="sl-card p-6">
              <h3 className="font-heading font-semibold text-slate-900">Recent POs</h3>
              <div className="mt-3 divide-y divide-slate-100">
                {(data.recent_pos || []).length === 0 && <div className="text-sm text-slate-500 py-6">No POs yet. Create an RFQ to get started.</div>}
                {(data.recent_pos || []).map((p) => (
                  <div key={p.id} className="py-3 flex items-center justify-between text-sm">
                    <div>
                      <div className="font-semibold text-slate-800">{p.po_number}</div>
                      <div className="text-slate-500 font-mono text-xs">{p.supplier_ref || '—'}</div>
                    </div>
                    <div className="text-slate-500 text-xs font-medium capitalize">{p.status || 'processing'}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
